import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sprout, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../lib/auth";
import { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F9F8F6]" data-testid="login-page">
      {/* Left: form */}
      <div className="flex flex-col justify-center w-full lg:w-1/2 px-6 sm:px-12 lg:px-20 py-10">
        <div className="flex items-center gap-3 mb-12">
          <div className="w-11 h-11 rounded-xl bg-[#1E3F20] grid place-items-center">
            <Sprout className="w-6 h-6 text-[#DDA15E]" strokeWidth={2.25} />
          </div>
          <div>
            <div className="font-heading font-black text-2xl leading-none text-[#1A2E1C]">Taraigold</div>
            <div className="text-xs font-semibold tracking-widest uppercase text-[#5C6B5E] mt-1">Field Force</div>
          </div>
        </div>

        <div className="max-w-md w-full">
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#1A2E1C]">
            Drive the field.
            <br />
            From one console.
          </h1>
          <p className="mt-3 text-[#5C6B5E] text-base">
            Orders, collections, live routes, and targets — all in one place for your sales team.
          </p>

          <form onSubmit={handleSubmit} className="mt-10 space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C6B5E]">Email</label>
              <input
                type="email"
                value={email}
                required
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="mt-2 w-full border border-[#E5E2DC] bg-white rounded-lg px-4 py-3 text-[#1A2E1C] placeholder-[#5C6B5E] focus:outline-none focus:ring-2 focus:ring-[#1E3F20] focus:border-transparent"
                placeholder="Enter your email id"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C6B5E]">Password</label>
              <div className="relative mt-2">
                <input
                  type={showPw ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="w-full border border-[#E5E2DC] bg-white rounded-lg px-4 py-3 pr-11 text-[#1A2E1C] focus:outline-none focus:ring-2 focus:ring-[#1E3F20] focus:border-transparent"
                  placeholder="Enter your password"
                />
                <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6B5E] hover:text-[#1E3F20]">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full bg-[#1E3F20] text-white rounded-lg px-4 py-3 font-semibold hover:bg-[#2C5530] transition-colors disabled:opacity-60"
            >
              {busy ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>
      </div>

      {/* Right: visual */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#FFF9E5] overflow-hidden items-center justify-center">
        <img
          src="https://customer-assets-wrfwihn1.emergentagent.net/job_sales-force-hub-8/artifacts/mcij15qd_be2edc04-12e5-4258-a859-17ec7ad7424a.png"
          alt="Taraigold — Nurturing Growth, Enriching Lives · Trusted by Farmers, Chosen for Generations"
          className="max-h-full max-w-full object-contain"
          data-testid="login-hero-image"
        />
      </div>
    </div>
  );
}
